import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  memo,
  useMemo,
} from 'react';
import { AgGridReact } from 'ag-grid-react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import AddRowButton from '../components/AddRowButton';
import { colors, COLUMN_TYPES } from '../constants';
import ValidationMessage from './ValidationMessage';
import { debounced, setColumnsWidth } from 'utils/helpers';

const GridTableContainer = styled.div`
  position: relative;
  height: calc(100vh - ${props => props.offsetTop}px);
  overflow: auto;
  > div {
    height: auto !important;
  }
`;

const Link = styled.a`
  color: ${colors.green};
`;

export const GridTable = memo(
  React.forwardRef(
    (
      {
        rows,
        defaultRowsNumber,
        columnDefs,
        onAddItems,
        onUpdateItem,
        onSelectItem,
        onCellClicked,
        rowSelection,
        isEditable,
        errors,
      },
      gridRef,
    ) => {
      const containerRef = useRef();
      const [tableOffsetTop, setTableOffsetTop] = useState(0);

      const getRowData = useCallback(() => {
        const rowData = [];
        gridRef.current.api.forEachNode(node => {
          rowData.push(node.data);
        });
        return rowData;
      }, []); // eslint-disable-line

      const handleAddRowButtonClick = useCallback(() => {
        onAddItems(1);
      }, []); // eslint-disable-line

      const onCellValueChanged = useCallback(cell => {
        const data = getRowData();
        onUpdateItem(data, cell);
      }, []); // eslint-disable-line

      const handleRowDataUpdate = useCallback(() => {
        onSelectItem();
      }, []); // eslint-disable-line

      const onGridReady = useCallback(() => {
        // add up to defaultRowsNumber new items
        if (
          defaultRowsNumber &&
          rows?.length < defaultRowsNumber &&
          isEditable
        ) {
          onAddItems(defaultRowsNumber - rows.length);
        }
      }, [isEditable]); // eslint-disable-line

      useEffect(() => {
        // need to calculate table height depends on different size of screen
        setTableOffsetTop(containerRef.current.offsetTop);
      }, [containerRef?.current?.offsetTop]);

      const defaultColDef = useMemo(
        () => ({
          flex: props => {
            return !props.colDef.width && 1;
          },
          resizable: true,
          cellStyle: props => {
            let defaultCellStyle = {};
            if (
              props.colDef.type === 'tags' ||
              (Array.isArray(props.colDef.type) &&
                props.colDef.type.includes('tags'))
            ) {
              defaultCellStyle = {
                display: 'flex',
                padding: '0 8px',
                alignItems: 'center',
              };
            }
            const error = errors.find(
              err =>
                err.row_id === props.data.row_id &&
                err.field === props.colDef.field,
            );

            if (error) {
              return {
                border: `${colors.orange} 1px solid`,
                overflow: 'visible',
                ...defaultCellStyle,
              };
            }
            return defaultCellStyle;
          },
          cellRenderer: props => {
            const error = errors.find(
              err =>
                err.row_id === props.data.row_id &&
                err.field === props.colDef.field,
            );
            return (
              <>
                {props.colDef.field === 'link' ? (
                  <Link
                    href={props.data[props.colDef.field]}
                    target="_blank"
                    rel="noreferrer">
                    {props.value}
                  </Link>
                ) : (
                  props.value
                )}
                {error && <ValidationMessage text={error.msg} />}
              </>
            );
          },
        }),
        [errors],
      );

      const handleColumnResize = debounced(props => {
        if (props.column) {
          setColumnsWidth(props.column);
        }
      }, 200);

      return (
        <GridTableContainer
          ref={containerRef}
          offsetTop={tableOffsetTop}
          className="ag-theme-material">
          <AgGridReact
            ref={gridRef}
            rowData={rows}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowBuffer={0}
            rowSelection={rowSelection}
            cacheBlockSize={100}
            cacheOverflowSize={2}
            maxConcurrentDatasourceRequests={1}
            infiniteInitialRowCount={1000}
            maxBlocksInCache={10}
            onGridReady={onGridReady}
            onCellValueChanged={onCellValueChanged}
            onSelectionChanged={onSelectItem}
            onRowDataUpdated={handleRowDataUpdate}
            onCellClicked={onCellClicked}
            onColumnResized={handleColumnResize}
            columnTypes={COLUMN_TYPES}
            domLayout="autoHeight"
            suppressRowClickSelection
            suppressRowTransform
          />
          {isEditable && <AddRowButton onClick={handleAddRowButtonClick} />}
        </GridTableContainer>
      );
    },
  ),
);

GridTable.propTypes = {
  rows: PropTypes.array,
  defaultRowsNumber: PropTypes.number,
  columnDefs: PropTypes.array.isRequired,
  errors: PropTypes.arrayOf(
    PropTypes.exact({
      msg: PropTypes.string.isRequired,
      row_id: PropTypes.string.isRequired,
      field: PropTypes.string.isRequired,
    }),
  ),
  onAddItems: PropTypes.func,
  onUpdateItem: PropTypes.func,
  onSelectItem: PropTypes.func,
  rowSelection: PropTypes.string,
  isEditable: PropTypes.bool,
};

GridTable.defaultProps = {
  rows: [],
  errors: [],
  defaultRowsNumber: 0,
  onAddItems: () => {},
  onUpdateItem: () => {},
  onSelectItem: () => {},
  onCellClicked: () => {},
  isEditable: true,
};
